import { useState } from "react";
import {
  HOURLY_RECURRING_OPTIONS,
  HOURLY_SEARCH_PATTERNS,
  listHourlyPackageChoices,
} from "../../config/hourlyMarketplaceSearch";
import {
  CAREHUB_RECURRING_OCCURRENCE_COUNT,
  clampDaysServiceRequired,
  DAYS_SERVICE_REQUIRED_MAX,
  DAYS_SERVICE_REQUIRED_MIN,
  recurringPatternNeedsDatePicker,
  todayIsoLocal,
} from "../../utils/careHubBookingRecurrence";
import { showToast } from "../../utils/toast";
import "./HourlyPreSearchPanel.css";

function formatDisplayDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d}-${m}-${y}`;
}

/**
 * Hourly package / pattern / slots UI.
 * `section`: "all" | "package" | "pattern" | "slots" for wizard screens.
 */
export default function HourlyPreSearchPanel({
  packageKey,
  onPackageKeyChange,
  pattern,
  onPatternChange,
  recurringPattern = "daily",
  onRecurringPatternChange,
  daysServiceRequired = CAREHUB_RECURRING_OCCURRENCE_COUNT,
  onDaysServiceRequiredChange,
  selectedDates = [],
  onSelectedDatesChange,
  searchDate,
  onSearchDateChange,
  availableSlots = [],
  selectedSlot = null,
  onSelectSlot,
  slotsReady = false,
  loadingSlots = false,
  onGenerateSlots,
  canGenerateSlots = false,
  slotSelected = false,
  embedded = false,
  section = "all",
}) {
  const packages = listHourlyPackageChoices();
  const minDate = todayIsoLocal();
  const showPackage = section === "all" || section === "package";
  const showPattern = section === "all" || section === "pattern";
  const showSlots = section === "all" || section === "slots";
  const showDaysRequired = pattern === "recurring" && typeof onDaysServiceRequiredChange === "function";
  const daysValue = clampDaysServiceRequired(daysServiceRequired, CAREHUB_RECURRING_OCCURRENCE_COUNT);
  const needsListedDates =
    pattern === "recurring" && recurringPatternNeedsDatePicker(recurringPattern);
  const listedDates = Array.isArray(selectedDates) ? selectedDates : [];
  const [customDateInput, setCustomDateInput] = useState(() => searchDate || todayIsoLocal());

  function bumpDays(delta) {
    const next = clampDaysServiceRequired(Number(daysValue) + delta, 1);
    onDaysServiceRequiredChange?.(next);
    if (listedDates.length > next) {
      const trimmed = listedDates.slice(0, next);
      onSelectedDatesChange?.(trimmed);
      if (trimmed.length) onSearchDateChange?.(trimmed[0]);
    }
  }

  function setDaysValue(raw) {
    const next = clampDaysServiceRequired(raw, daysValue);
    onDaysServiceRequiredChange?.(next);
    if (listedDates.length > next) {
      const trimmed = listedDates.slice(0, next);
      onSelectedDatesChange?.(trimmed);
      if (trimmed.length) onSearchDateChange?.(trimmed[0]);
    }
  }

  function addListedDate() {
    if (!customDateInput) return;
    if (customDateInput < minDate) {
      showToast.error("Pick today or a future date.");
      return;
    }
    if (listedDates.includes(customDateInput)) {
      showToast.info("That date is already added.");
      return;
    }
    if (listedDates.length >= daysValue) {
      showToast.info(
        `You selected ${daysValue} day${daysValue === 1 ? "" : "s"} of service. Remove a date to add another.`
      );
      return;
    }
    const next = [...listedDates, customDateInput].sort();
    onSelectedDatesChange?.(next);
    onSearchDateChange?.(next[0]);
  }

  function removeListedDate(iso) {
    const next = listedDates.filter((d) => d !== iso);
    onSelectedDatesChange?.(next);
    if (next.length) onSearchDateChange?.(next[0]);
  }

  const daysCounter = showDaysRequired ? (
    <div
      className="hourly-pre-search__days-field"
      role="group"
      aria-label="Number of days service required"
    >
      <span className="hourly-pre-search__days-label">No. of days service required</span>
      <div className="hourly-pre-search__days-counter">
        <button
          type="button"
          className="hourly-pre-search__days-btn"
          aria-label="Decrease days"
          disabled={daysValue <= DAYS_SERVICE_REQUIRED_MIN}
          onClick={() => bumpDays(-1)}
        >
          −
        </button>
        <input
          type="number"
          className="hourly-pre-search__days-input"
          min={DAYS_SERVICE_REQUIRED_MIN}
          max={DAYS_SERVICE_REQUIRED_MAX}
          value={daysValue}
          aria-label="Days of service required"
          onChange={(e) => setDaysValue(e.target.value)}
        />
        <button
          type="button"
          className="hourly-pre-search__days-btn"
          aria-label="Increase days"
          disabled={daysValue >= DAYS_SERVICE_REQUIRED_MAX}
          onClick={() => bumpDays(1)}
        >
          +
        </button>
        <span className="hourly-pre-search__days-unit">
          day{daysValue === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  ) : null;

  return (
    <section
      className={`hourly-pre-search${embedded ? " hourly-pre-search--embedded" : ""}`}
      aria-label="Hourly care package and schedule"
    >
      {!embedded ? (
        <div className="hourly-pre-search__head">
          <h2 className="hourly-pre-search__title">Choose your hourly care</h2>
          <p className="hourly-pre-search__lead">
            Pick a package, scheduling pattern, and available time — then search caregivers for that slot.
          </p>
        </div>
      ) : null}

      {showPackage ? (
        <div className="hourly-pre-search__block">
          {section === "all" ? <h3 className="hourly-pre-search__label">1. Care package</h3> : null}
          <div className="hourly-pre-search__packs" role="group" aria-label="Hourly care package">
            {packages.map((pack) => {
              const selected = packageKey === pack.key;
              return (
                <button
                  key={pack.key}
                  type="button"
                  className={`hourly-pre-search__pack${selected ? " is-selected" : ""}`}
                  onClick={() => onPackageKeyChange?.(pack.key)}
                  aria-pressed={selected}
                >
                  <span className="hourly-pre-search__pack-badge">{pack.badge}</span>
                  <strong className="hourly-pre-search__pack-title">{pack.title}</strong>
                  <span className="hourly-pre-search__pack-meta">
                    {pack.durationLabel}
                    {pack.defaultPriceInr != null ? ` · from ₹${pack.defaultPriceInr}` : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {showPattern ? (
        <div className="hourly-pre-search__block">
          {section === "all" ? <h3 className="hourly-pre-search__label">2. Scheduling pattern</h3> : null}
          <div className="hourly-pre-search__patterns" role="group" aria-label="Scheduling pattern">
            {HOURLY_SEARCH_PATTERNS.map((p) => {
              const selected = pattern === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`hourly-pre-search__pattern${selected ? " is-selected" : ""}`}
                  onClick={() => onPatternChange?.(p.id)}
                  aria-pressed={selected}
                >
                  <span
                    className={`hourly-pre-search__pattern-badge hourly-pre-search__pattern-badge--${p.badgeTone}`}
                  >
                    {p.badge}
                  </span>
                  <strong>{p.title}</strong>
                  <span>{p.description}</span>
                </button>
              );
            })}
          </div>

          {pattern === "recurring" ? (
            <div className="hourly-pre-search__recurring" role="group" aria-label="Recurring frequency">
              {HOURLY_RECURRING_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`hourly-pre-search__recurring-opt${
                    recurringPattern === opt.id ? " is-selected" : ""
                  }`}
                  onClick={() => onRecurringPatternChange?.(opt.id)}
                  aria-pressed={recurringPattern === opt.id}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {showSlots ? (
        <div className="hourly-pre-search__block">
          {section === "all" ? (
            <h3 className="hourly-pre-search__label">3. Date &amp; available slots</h3>
          ) : null}

          {needsListedDates ? (
            <>
              <p className="hourly-pre-search__listed-hint">
                {recurringPattern === "alternate"
                  ? "Select alternate dates"
                  : "Select custom dates"}
                {" — "}
                add up to {daysValue} future date{daysValue === 1 ? "" : "s"} for this booking.
              </p>
              <div className="hourly-pre-search__date-row">
                <label className="hourly-pre-search__date-field">
                  <span>Add booking date</span>
                  <input
                    type="date"
                    value={customDateInput || ""}
                    min={minDate}
                    onChange={(e) => setCustomDateInput(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="hourly-pre-search__add-date"
                  onClick={addListedDate}
                >
                  Add Date
                </button>
                {daysCounter}
              </div>
              {listedDates.length ? (
                <div className="hourly-pre-search__date-chips" aria-label="Selected service dates">
                  {listedDates.map((iso) => (
                    <span key={iso} className="hourly-pre-search__date-chip">
                      {formatDisplayDate(iso)}
                      <button
                        type="button"
                        aria-label={`Remove ${formatDisplayDate(iso)}`}
                        onClick={() => removeListedDate(iso)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="hourly-pre-search__listed-hint hourly-pre-search__listed-hint--muted">
                  Add {daysValue} date{daysValue === 1 ? "" : "s"}, then generate slots.
                </p>
              )}
              <div className="hourly-pre-search__date-row hourly-pre-search__date-row--actions">
                <button
                  type="button"
                  className="hourly-pre-search__generate"
                  onClick={() => onGenerateSlots?.()}
                  disabled={!canGenerateSlots || loadingSlots || !listedDates.length}
                >
                  {loadingSlots ? "Finding slots…" : "Generate slots"}
                </button>
              </div>
            </>
          ) : (
            <div className="hourly-pre-search__date-row">
              <label className="hourly-pre-search__date-field">
                <span>Service date</span>
                <input
                  type="date"
                  value={searchDate || ""}
                  min={minDate}
                  onChange={(e) => onSearchDateChange?.(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="hourly-pre-search__generate"
                onClick={() => onGenerateSlots?.()}
                disabled={!canGenerateSlots || loadingSlots}
              >
                {loadingSlots ? "Finding slots…" : "Generate slots"}
              </button>
              {daysCounter}
            </div>
          )}

          {showDaysRequired && daysValue > 30 ? (
            <p className="hourly-pre-search__days-hint" role="status">
              Continuous care beyond 30 days needs a Quarterly Care Pass.
            </p>
          ) : null}

          {slotsReady && availableSlots.length === 0 && !loadingSlots ? (
            <p className="hourly-pre-search__empty" role="status">
              No open slots for this date and package. Try another day or package.
            </p>
          ) : null}

          {availableSlots.length > 0 ? (
            <div className="hourly-pre-search__slots" role="listbox" aria-label="Available time slots">
              {availableSlots.map((slot) => {
                const selected =
                  selectedSlot &&
                  String(selectedSlot.start) === String(slot.start) &&
                  String(selectedSlot.end) === String(slot.end);
                return (
                  <button
                    key={slot.id || `${slot.start}-${slot.end}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`hourly-pre-search__slot${selected ? " is-selected" : ""}`}
                    onClick={() => onSelectSlot?.(slot)}
                  >
                    {slot.label || `${slot.start} – ${slot.end}`}
                  </button>
                );
              })}
            </div>
          ) : null}

          {slotSelected ? (
            <p className="hourly-pre-search__ready" role="status">
              {embedded ? (
                <>Loading caregivers for this time…</>
              ) : (
                <>
                  Slot selected. Tap <strong>Search Caregivers</strong> above to see matching caregivers.
                </>
              )}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
